/**
 * UnoAI — AI opponents for UNO
 *
 * Three difficulty levels:
 *   Easy:   Random valid card, 500ms delay
 *   Medium: Prefer action cards, match color when possible, 1s delay
 *   Hard:   Strategic play — save wilds, play draw cards offensively,
 *           color management, track opponent card counts, 2s delay
 */

import {
  UnoCard, UnoColor, UnoDifficulty, UnoGameState,
  VisibleUnoGameState, UNO_COLORS,
} from './types';
import { UnoEngine } from './UnoEngine';

// ============================================================================
// Helper
// ============================================================================

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function randomInRange(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

// ============================================================================
// UnoAI Class
// ============================================================================

export class UnoAI {
  difficulty: UnoDifficulty;
  testMode: boolean = false;

  constructor(difficulty: UnoDifficulty = 'easy') {
    this.difficulty = difficulty;
  }

  /**
   * Decide which card to play (or draw).
   * Returns { action: 'play', card, chosenColor? } or { action: 'draw' }.
   */
  async getAction(
    engine: UnoEngine,
    playerId: string,
  ): Promise<{ action: 'play'; card: UnoCard; chosenColor?: UnoColor } | { action: 'draw' }> {
    if (!this.testMode) {
      const ms = this.getThinkingDelay();
      await delay(ms);
    }

    const validMoves = engine.getValidMoves(playerId);

    if (validMoves.length === 0) {
      return { action: 'draw' };
    }

    switch (this.difficulty) {
      case 'easy': return this.getEasyAction(validMoves);
      case 'medium': return this.getMediumAction(validMoves, engine, playerId);
      case 'hard': return this.getHardAction(validMoves, engine, playerId);
      default: return this.getEasyAction(validMoves);
    }
  }

  /**
   * Choose a color when playing a wild card.
   */
  chooseColor(hand: UnoCard[]): UnoColor {
    // Count colors in hand
    const colorCounts: Record<UnoColor, number> = { red: 0, blue: 0, green: 0, yellow: 0 };
    for (const card of hand) {
      if (card.color) colorCounts[card.color]++;
    }

    // Pick the most common color
    let bestColor: UnoColor = 'red';
    let bestCount = -1;
    for (const color of UNO_COLORS) {
      if (colorCounts[color] > bestCount) {
        bestCount = colorCounts[color];
        bestColor = color;
      }
    }

    // If no colored cards, pick random
    if (bestCount === 0) return pickRandom(UNO_COLORS);

    return bestColor;
  }

  /**
   * Decide whether to play a drawn card.
   */
  shouldPlayDrawnCard(card: UnoCard, engine: UnoEngine, playerId: string): boolean {
    const validMoves = engine.getValidMoves(playerId);
    const canPlay = validMoves.some(c => c.id === card.id);
    if (!canPlay) return false;

    // Easy: always play if possible
    if (this.difficulty === 'easy') return true;

    // Medium/Hard: don't play wild draw four if you have other options
    if (card.type === 'wild-draw-four' && this.difficulty === 'hard') {
      return false; // Save it
    }

    return true;
  }

  // =========================================================================
  // Easy AI — Random valid card
  // =========================================================================

  private getEasyAction(
    validMoves: UnoCard[],
  ): { action: 'play'; card: UnoCard; chosenColor?: UnoColor } {
    const card = pickRandom(validMoves);
    const chosenColor = (card.type === 'wild' || card.type === 'wild-draw-four')
      ? pickRandom(UNO_COLORS)
      : undefined;

    return { action: 'play', card, chosenColor };
  }

  // =========================================================================
  // Medium AI — Prefer action cards, match color
  // =========================================================================

  private getMediumAction(
    validMoves: UnoCard[],
    engine: UnoEngine,
    playerId: string,
  ): { action: 'play'; card: UnoCard; chosenColor?: UnoColor } {
    const state = engine.getGameState();
    const player = state.players.find(p => p.id === playerId);
    const hand = player?.hand || [];

    // Priority: action cards > color match > wilds
    const actionCards = validMoves.filter(c =>
      c.type === 'skip' || c.type === 'reverse' || c.type === 'draw-two'
    );
    const colorMatch = validMoves.filter(c =>
      c.color !== null && c.type === 'number'
    );
    const wilds = validMoves.filter(c =>
      c.type === 'wild' || c.type === 'wild-draw-four'
    );

    let card: UnoCard;

    if (actionCards.length > 0) {
      card = pickRandom(actionCards);
    } else if (colorMatch.length > 0) {
      card = pickRandom(colorMatch);
    } else if (wilds.length > 0) {
      card = pickRandom(wilds);
    } else {
      card = pickRandom(validMoves);
    }

    const chosenColor = (card.type === 'wild' || card.type === 'wild-draw-four')
      ? this.chooseColor(hand)
      : undefined;

    return { action: 'play', card, chosenColor };
  }

  // =========================================================================
  // Hard AI — Strategic play
  // =========================================================================

  private getHardAction(
    validMoves: UnoCard[],
    engine: UnoEngine,
    playerId: string,
  ): { action: 'play'; card: UnoCard; chosenColor?: UnoColor } {
    const state = engine.getGameState();
    const player = state.players.find(p => p.id === playerId);
    const hand = player?.hand || [];

    // Check if next player is close to winning
    const nextPlayerIndex = this.getNextPlayerIndex(state.currentPlayerIndex, state.direction, state.players.length);
    const nextPlayer = state.players[nextPlayerIndex];
    const nextPlayerDangerous = nextPlayer && nextPlayer.hand.length <= 2;

    // If next player is dangerous, prioritize draw/skip cards
    if (nextPlayerDangerous) {
      const drawCards = validMoves.filter(c =>
        c.type === 'draw-two' || c.type === 'wild-draw-four'
      );
      if (drawCards.length > 0) {
        const card = drawCards[0];
        const chosenColor = (card.type === 'wild-draw-four')
          ? this.chooseColor(hand)
          : undefined;
        return { action: 'play', card, chosenColor };
      }

      const skipCards = validMoves.filter(c => c.type === 'skip');
      if (skipCards.length > 0) {
        return { action: 'play', card: skipCards[0] };
      }

      const reverseCards = validMoves.filter(c => c.type === 'reverse');
      if (reverseCards.length > 0 && state.players.length > 2) {
        return { action: 'play', card: reverseCards[0] };
      }
    }

    // Save wilds for when needed — play non-wild cards first
    const nonWilds = validMoves.filter(c =>
      c.type !== 'wild' && c.type !== 'wild-draw-four'
    );

    if (nonWilds.length > 0) {
      // Score each card
      const scored = nonWilds.map(card => ({
        card,
        score: this.scoreCard(card, hand, state),
      }));
      scored.sort((a, b) => b.score - a.score);

      const card = scored[0].card;
      return { action: 'play', card };
    }

    // Must play a wild
    // Prefer regular wild over wild draw four (save the big gun)
    const regularWilds = validMoves.filter(c => c.type === 'wild');
    const wildDrawFours = validMoves.filter(c => c.type === 'wild-draw-four');

    const card = regularWilds.length > 0 ? regularWilds[0] : wildDrawFours[0];
    const chosenColor = this.chooseColor(hand);

    return { action: 'play', card, chosenColor };
  }

  /**
   * Score a non-wild card for strategic play.
   * Higher score = better to play now.
   */
  private scoreCard(card: UnoCard, hand: UnoCard[], state: UnoGameState): number {
    let score = 0;

    // Prefer playing action cards (they're more disruptive)
    if (card.type === 'draw-two') score += 15;
    if (card.type === 'skip') score += 10;
    if (card.type === 'reverse') score += 8;

    // Prefer playing cards of colors we have many of (keeps options open)
    if (card.color) {
      const sameColorCount = hand.filter(c => c.color === card.color).length;
      score += sameColorCount * 2;
    }

    // Prefer higher number cards (they're worth more points if opponent wins)
    if (card.type === 'number' && card.value !== null) {
      score += card.value;
    }

    return score;
  }

  private getNextPlayerIndex(current: number, direction: string, count: number): number {
    if (direction === 'clockwise') {
      return (current + 1) % count;
    }
    return (current - 1 + count) % count;
  }

  private getThinkingDelay(): number {
    switch (this.difficulty) {
      case 'easy': return randomInRange(400, 800);
      case 'medium': return randomInRange(800, 1500);
      case 'hard': return randomInRange(1500, 2500);
      default: return randomInRange(400, 800);
    }
  }

  setDifficulty(difficulty: UnoDifficulty): void {
    this.difficulty = difficulty;
  }

  reset(): void {
    // No persistent state to reset for UNO AI
  }
}
